import { use, useRef, useState } from "react";
import { AnalyticsEvents, track } from "@/lib/analytics";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { AuthUIContext } from "@papier/auth-ui";
import type { ApiKeyCreateUpdateFormSchema } from "@papier/api-keys-contracts";
import { KeyMinimalistic as KeyRound, Add as Plus } from "@papier/ui/components/icons";
import { toast } from "sonner";
import { orpcClient } from "@/lib/orpc";
import { ApiKeyForm } from "@/components/orgs/api-key-form";
import { ApiKeysList } from "@/components/orgs/api-keys-list";
import { ApiKeySecretDialog } from "@/components/orgs/api-key-secret-dialog";
import { OrganizationPageHeader } from "@/components/orgs/organization-page";
import { PendingApiKeysComponent } from "@/components/pending";
import { Button } from "@papier/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@papier/ui/components/ui/dialog";
import type { RouterInputs } from "@papier/api";

export const Route = createFileRoute("/_app/organization/api-keys")({
  component: RouteComponent,
  head: () => ({ meta: [{ title: "Papier - API keys" }] }),
});

const keyTypes = [
  [
    "Server application",
    "Fetch published templates and send from your backend. Keep this key on your server.",
  ],
  ["CI sync", "Read template contracts in your build pipeline. Cannot fetch message content."],
];

function RouteComponent() {
  const { queryClient, orpc } = Route.useRouteContext();
  const {
    authClient,
    hooks: { useHasPermission },
  } = use(AuthUIContext);
  const { data: organization, isPending } = authClient.useActiveOrganization();
  const permission = useHasPermission({
    organizationId: organization?.id,
    permissions: { organization: ["update"] },
  });
  const [createOpen, setCreateOpen] = useState(false);
  const createButton = useRef<HTMLButtonElement>(null);
  const [createdKey, setCreatedKey] = useState<{ name: string; secret: string } | null>(null);
  const create = useMutation({
    mutationFn: async (input: RouterInputs["apiKeys"]["create"]) => {
      const created = await orpcClient.apiKeys.create(input);
      // The secret lives only in this one-time dialog, never in mutation data or browser storage.
      setCreatedKey({ name: created.name, secret: created.secret });
    },
    onSuccess: (_data, input) => {
      track(AnalyticsEvents.ApiKeyCreated, { scope: input.scope });
      setCreateOpen(false);
      toast.success("API key created successfully");
      return queryClient.invalidateQueries({ queryKey: orpc.apiKeys.getAll.key() });
    },
  });
  const onCreateSubmit = async (data: ApiKeyCreateUpdateFormSchema) => {
    try {
      await create.mutateAsync({
        name: data.name,
        scope: data.scope,
        expiresAt: data.expiresAt ?? null,
        authorizationConfig: data.access === "WHOLE_ORG" ? "WHOLE_ORG" : data.projects,
        ...(data.scope === "publishable" ? { origins: data.origins ?? [] } : {}),
      });
    } catch {
      /* The form displays the mutation error and retains the entered values. */
    }
  };
  if (isPending || permission.isPending) return <PendingApiKeysComponent />;
  return (
    <main key={organization?.id} className="w-full min-w-0 space-y-7">
      <OrganizationPageHeader
        title="API keys"
        description="Connect your server or CI pipeline to Papier. Manage browser keys in your project’s Web Push settings."
        actions={
          permission.data?.success && (
            <Button
              ref={createButton}
              onClick={() => {
                create.reset();
                setCreateOpen(true);
              }}
            >
              <Plus className="size-4" aria-hidden="true" />
              Create API key
            </Button>
          )
        }
      />
      <details className="rounded-xl border bg-muted/20 p-4 md:hidden">
        <summary className="cursor-pointer text-sm font-medium">Choose the right key type</summary>
        <dl className="mt-4 space-y-4 text-xs">
          {keyTypes.map(([title, description]) => (
            <div key={title}>
              <dt className="font-semibold">{title}</dt>
              <dd className="mt-1 leading-relaxed text-muted-foreground">{description}</dd>
            </div>
          ))}
        </dl>
      </details>
      <div className="hidden gap-3 md:grid md:grid-cols-2">
        {keyTypes.map(([title, description]) => (
          <div key={title} className="rounded-xl border border-border/60 bg-muted/20 p-4">
            <h2 className="flex items-center gap-2 text-xs font-semibold">
              <KeyRound className="size-3.5 text-muted-foreground" aria-hidden="true" />
              {title}
            </h2>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{description}</p>
          </div>
        ))}
      </div>
      <ApiKeysList hasPermission={permission.data?.success} />
      <Dialog
        open={createOpen && permission.data?.success === true}
        onOpenChange={(open) => {
          if (!create.isPending) setCreateOpen(open);
        }}
      >
        <DialogContent
          className="sm:max-w-2xl"
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (!createdKey) createButton.current?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle>Create an API key</DialogTitle>
            <DialogDescription>
              Choose what this integration needs to do and which projects it can access.
            </DialogDescription>
          </DialogHeader>
          <ApiKeyForm
            onSubmit={onCreateSubmit}
            isSuccess={create.isSuccess}
            error={create.error?.message}
          />
        </DialogContent>
      </Dialog>
      <ApiKeySecretDialog
        createdKey={createOpen ? null : createdKey}
        onClose={() => {
          setCreatedKey(null);
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          createButton.current?.focus();
        }}
      />
    </main>
  );
}
